// SNIGJ - Admin User Management Page
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { t } from '../lib/i18n';
import DashboardLayout from '../components/layout/DashboardLayout';
import UserProfileCard from '../components/UserProfileCard';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import { 
  Users, UserPlus, Edit, Trash2, Search, Filter, Shield, MapPin,
  Key, Eye, EyeOff, Save, X, RefreshCw, Lock, Unlock, Building2,
  AlertTriangle, CheckCircle, Mail, Phone, Calendar, Gavel, Scale,
  Award, Hash, FileText
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

// Role categories for grouping
const ROLE_CATEGORIES = {
  PUBLIC: { label: { fr: 'Public', en: 'Public' }, color: 'bg-gray-500' },
  POLICE: { label: { fr: 'Police', en: 'Police' }, color: 'bg-blue-600' },
  BORDER_CONTROL: { label: { fr: 'Contrôle Frontières', en: 'Border Control' }, color: 'bg-orange-500' },
  TRIBUNAL_PAIX: { label: { fr: 'Tribunal de Paix', en: 'Peace Court' }, color: 'bg-cyan-500' },
  PARQUET_GI: { label: { fr: 'Parquet Grande Instance', en: 'High Instance Prosecution' }, color: 'bg-indigo-500' },
  COUR_APPEL: { label: { fr: "Cour d'Appel", en: 'Court of Appeal' }, color: 'bg-purple-500' },
  PARQUET_GENERAL: { label: { fr: 'Parquet Général', en: 'General Prosecution' }, color: 'bg-violet-500' },
  COUR_CASSATION: { label: { fr: 'Cour de Cassation', en: 'Court of Cassation' }, color: 'bg-pink-500' },
  CONSEIL_ETAT: { label: { fr: "Conseil d'État", en: 'Council of State' }, color: 'bg-emerald-500' },
  COUR_SUPREME: { label: { fr: 'Cour Suprême', en: 'Supreme Court' }, color: 'bg-amber-600' },
  COUR_COMPTES: { label: { fr: 'Cour des Comptes', en: 'Court of Auditors' }, color: 'bg-teal-500' },
  AUXILIAIRES_JUSTICE: { label: { fr: 'Auxiliaires de Justice', en: 'Legal Auxiliaries' }, color: 'bg-cyan-600' },
  PRISON: { label: { fr: 'Prison', en: 'Prison' }, color: 'bg-red-600' },
  ADMINISTRATION: { label: { fr: 'Administration', en: 'Administration' }, color: 'bg-yellow-600' },
  OVERSIGHT: { label: { fr: 'Contrôle', en: 'Oversight' }, color: 'bg-green-600' },
  EXECUTIVE: { label: { fr: 'Exécutif', en: 'Executive' }, color: 'bg-amber-700' },
  SYSTEM: { label: { fr: 'Système', en: 'System' }, color: 'bg-red-700' },
  OTHER: { label: { fr: 'Autre', en: 'Other' }, color: 'bg-gray-600' }
};

// Clearance levels for classified documents
const CLEARANCE_LEVELS = [
  { value: 'PUBLIC', label: { fr: 'Public', en: 'Public' }, level: 0, color: 'bg-green-500' },
  { value: 'RESTRICTED', label: { fr: 'Restreint', en: 'Restricted' }, level: 1, color: 'bg-blue-500' },
  { value: 'CONFIDENTIAL', label: { fr: 'Confidentiel', en: 'Confidential' }, level: 2, color: 'bg-yellow-500' },
  { value: 'SECRET', label: { fr: 'Secret', en: 'Secret' }, level: 3, color: 'bg-orange-500' },
  { value: 'TOP_SECRET', label: { fr: 'Très Secret', en: 'Top Secret' }, level: 4, color: 'bg-red-500' }
];

// DRC Jurisdictions
const JURISDICTIONS = [
  { value: 'NATIONAL', label: 'National (Tout le pays)' },
  { value: 'KINSHASA', label: 'Kinshasa' },
  { value: 'KONGO_CENTRAL', label: 'Kongo-Central' },
  { value: 'HAUT_KATANGA', label: 'Haut-Katanga' },
  { value: 'NORD_KIVU', label: 'Nord-Kivu' },
  { value: 'SUD_KIVU', label: 'Sud-Kivu' },
  { value: 'KASAI', label: 'Kasaï' },
  { value: 'KASAI_CENTRAL', label: 'Kasaï-Central' },
  { value: 'KASAI_ORIENTAL', label: 'Kasaï-Oriental' },
  { value: 'LUALABA', label: 'Lualaba' },
  { value: 'HAUT_LOMAMI', label: 'Haut-Lomami' },
  { value: 'TANGANYIKA', label: 'Tanganyika' },
  { value: 'MANIEMA', label: 'Maniema' },
  { value: 'TSHOPO', label: 'Tshopo' },
  { value: 'ITURI', label: 'Ituri' },
  { value: 'BAS_UELE', label: 'Bas-Uele' },
  { value: 'HAUT_UELE', label: 'Haut-Uele' },
  { value: 'NORD_UBANGI', label: 'Nord-Ubangi' },
  { value: 'SUD_UBANGI', label: 'Sud-Ubangi' },
  { value: 'MONGALA', label: 'Mongala' },
  { value: 'EQUATEUR', label: 'Équateur' },
  { value: 'TSHUAPA', label: 'Tshuapa' },
  { value: 'MAI_NDOMBE', label: 'Maï-Ndombe' },
  { value: 'KWILU', label: 'Kwilu' },
  { value: 'KWANGO', label: 'Kwango' },
  { value: 'LOMAMI', label: 'Lomami' },
  { value: 'SANKURU', label: 'Sankuru' }
];

// Fallback permissions (used if API fails)
const DEFAULT_PERMISSIONS = [
  { key: 'view_cases', label: { fr: 'Voir les dossiers', en: 'View cases' } },
  { key: 'edit_cases', label: { fr: 'Modifier les dossiers', en: 'Edit cases' } },
  { key: 'create_cases', label: { fr: 'Créer des dossiers', en: 'Create cases' } },
  { key: 'delete_cases', label: { fr: 'Supprimer des dossiers', en: 'Delete cases' } },
  { key: 'view_prisoners', label: { fr: 'Voir les détenus', en: 'View prisoners' } },
  { key: 'manage_prisoners', label: { fr: 'Gérer les détenus', en: 'Manage prisoners' } },
  { key: 'view_documents', label: { fr: 'Voir les documents', en: 'View documents' } },
  { key: 'sign_documents', label: { fr: 'Signer les documents', en: 'Sign documents' } },
  { key: 'view_payments', label: { fr: 'Voir les paiements', en: 'View payments' } },
  { key: 'manage_payments', label: { fr: 'Gérer les paiements', en: 'Manage payments' } },
  { key: 'border_scan', label: { fr: 'Scanner aux frontières', en: 'Border scan' } },
  { key: 'manage_fugitives', label: { fr: 'Gérer les fugitifs', en: 'Manage fugitives' } },
  { key: 'view_statistics', label: { fr: 'Voir les statistiques', en: 'View statistics' } },
  { key: 'manage_users', label: { fr: 'Gérer les utilisateurs', en: 'Manage users' } },
  { key: 'system_config', label: { fr: 'Configuration système', en: 'System config' } }
];

const AdminUserManagementPage = () => {
  const { user, language } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [institutions, setInstitutions] = useState({ provincial: [], national: [], all: [] });
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [permissionCategories, setPermissionCategories] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterJurisdiction, setFilterJurisdiction] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [convertingUser, setConvertingUser] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  // New user form state
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    name: '',
    role: 'CITIZEN',
    phone: '',
    national_id: '',
    grade: '',
    matricule: '',
    clearance_level: 'PUBLIC',
    jurisdiction: 'KINSHASA',
    institution: '',
    jurisdictions: ['KINSHASA'],
    permissions: [],
    active: true,
    can_access_classified: false,
    max_classification_access: 'PUBLIC',
    // New fields
    is_military: false,
    service_branch: 'POLICE',
    assigned_structure_id: '',
    territorial_level: 'NATIONAL',
    province: '',
    module_access: []
  });

  // Convert citizen form state
  const [convertData, setConvertData] = useState({
    role: 'GREFFIER_PAIX',
    clearance_level: 'RESTRICTED',
    jurisdiction: 'KINSHASA',
    institution: '',
    grade: '',
    matricule: '',
    permissions: []
  });

  // Grades state
  const [grades, setGrades] = useState({});

  useEffect(() => {
    loadUsers();
    loadRoles();
    loadInstitutions();
    loadPermissions();
    loadGrades();
  }, []);

  const loadGrades = async () => {
    try {
      const response = await api.get('/admin/grades');
      setGrades(response.data.grades || {});
    } catch (error) {
      console.error('Failed to load grades:', error);
    }
  };

  const loadRoles = async () => {
    try {
      const response = await api.get('/admin/roles');
      setRoles(response.data.roles || []);
    } catch (error) {
      console.error('Failed to load roles:', error);
    }
  };

  const loadInstitutions = async () => {
    try {
      const response = await api.get('/admin/institutions');
      setInstitutions(response.data);
    } catch (error) {
      console.error('Failed to load institutions:', error);
    }
  };

  const loadPermissions = async () => {
    try {
      const response = await api.get('/admin/permissions');
      const perms = response.data.permissions || [];
      setAvailablePermissions(perms.map(p => ({
        key: p.key,
        label: { fr: p.label_fr, en: p.label_en },
        category: p.category,
        action: p.action
      })));
      setPermissionCategories(response.data.categories || {});
    } catch (error) {
      console.error('Failed to load permissions:', error);
      setAvailablePermissions(DEFAULT_PERMISSIONS);
    }
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/users');
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Failed to load users:', error);
      toast.error(language === 'fr' ? 'Erreur de chargement' : 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  // Convert citizen to user
  const handleConvertCitizen = async () => {
    if (!convertingUser) return;
    
    try {
      await api.post(`/admin/citizen-to-user/${convertingUser.user_id}`, convertData);
      toast.success(language === 'fr' ? 'Citoyen converti en utilisateur' : 'Citizen converted to user');
      setShowConvertModal(false);
      setConvertingUser(null);
      setConvertData({
        role: 'GREFFIER_PAIX',
        clearance_level: 'RESTRICTED',
        jurisdiction: 'KINSHASA',
        institution: '',
        permissions: []
      });
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error converting user');
    }
  };

  const openConvertModal = (userToConvert) => {
    setConvertingUser(userToConvert);
    setConvertData({
      role: 'GREFFIER_PAIX',
      clearance_level: 'RESTRICTED',
      jurisdiction: userToConvert.jurisdiction || 'KINSHASA',
      institution: '',
      permissions: []
    });
    setShowConvertModal(true);
  };

  const handleCreateUser = async () => {
    try {
      if (!newUser.email || !newUser.password || !newUser.name) {
        toast.error(language === 'fr' ? 'Remplissez tous les champs obligatoires' : 'Fill all required fields');
        return;
      }

      const payload = { ...newUser, send_verification: newUser.send_verification !== false };
      const response = await api.post('/admin/users', payload);
      
      if (response.data.requires_verification) {
        toast.success(
          language === 'fr' 
            ? `Utilisateur créé. Un email de vérification a été envoyé à ${newUser.email}.`
            : `User created. A verification email has been sent to ${newUser.email}.`
        );
      } else {
        toast.success(language === 'fr' ? 'Utilisateur créé avec succès' : 'User created successfully');
      }
      
      setShowCreateModal(false);
      resetForm();
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error creating user');
    }
  };

  const handleUpdateUser = async () => {
    try {
      await api.put(`/admin/users/${editingUser.user_id}`, newUser);
      toast.success(language === 'fr' ? 'Utilisateur mis à jour' : 'User updated');
      setEditingUser(null);
      setShowCreateModal(false);
      resetForm();
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error updating user');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!confirm(language === 'fr' ? 'Supprimer cet utilisateur?' : 'Delete this user?')) return;
    
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success(language === 'fr' ? 'Utilisateur supprimé' : 'User deleted');
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const handleToggleActive = async (userId, currentStatus) => {
    try {
      await api.put(`/admin/users/${userId}/status`, { active: !currentStatus });
      toast.success(language === 'fr' ? 'Statut mis à jour' : 'Status updated');
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const handleResendVerification = async (userId, email) => {
    try {
      await api.post(`/admin/users/${userId}/resend-verification`);
      toast.success(
        language === 'fr' 
          ? `Email de vérification renvoyé à ${email}` 
          : `Verification email resent to ${email}`
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Échec de l\'envoi' : 'Failed to send'));
    }
  };

  const handleForceVerify = async (userId, name) => {
    if (!confirm(language === 'fr' 
      ? `Vérifier manuellement le compte de ${name} sans envoyer d'email ?` 
      : `Manually verify ${name}'s account without sending an email?`)) return;
    try {
      await api.post(`/admin/users/${userId}/force-verify`);
      toast.success(language === 'fr' ? `Compte de ${name} vérifié et activé` : `${name}'s account verified and activated`);
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const handleEditUser = (userToEdit) => {
    setEditingUser(userToEdit);
    setNewUser({
      email: userToEdit.email,
      password: '',
      name: userToEdit.name,
      role: userToEdit.role,
      phone: userToEdit.phone || '',
      national_id: userToEdit.national_id || '',
      grade: userToEdit.grade || '',
      matricule: userToEdit.matricule || '',
      clearance_level: userToEdit.clearance_level || 'PUBLIC',
      jurisdiction: userToEdit.jurisdiction || 'KINSHASA',
      jurisdictions: userToEdit.jurisdictions || [userToEdit.jurisdiction || 'KINSHASA'],
      permissions: userToEdit.permissions || [],
      active: userToEdit.active !== false,
      can_access_classified: userToEdit.can_access_classified || false,
      max_classification_access: userToEdit.max_classification_access || 'PUBLIC',
      is_military: userToEdit.is_military || false,
      service_branch: userToEdit.service_branch || 'POLICE',
      assigned_structure_id: userToEdit.assigned_structure_id || '',
      territorial_level: userToEdit.territorial_level || 'NATIONAL',
      province: userToEdit.province || '',
      module_access: userToEdit.module_access || []
    });
    setShowCreateModal(true);
  };

  const resetForm = () => {
    setNewUser({
      email: '',
      password: '',
      name: '',
      role: 'CITIZEN',
      phone: '',
      national_id: '',
      grade: '',
      matricule: '',
      clearance_level: 'PUBLIC',
      jurisdiction: 'KINSHASA',
      jurisdictions: ['KINSHASA'],
      permissions: [],
      active: true,
      can_access_classified: false,
      max_classification_access: 'PUBLIC',
      is_military: false,
      service_branch: 'POLICE',
      assigned_structure_id: '',
      territorial_level: 'NATIONAL',
      province: ''
    });
    setEditingUser(null);
  };

  const togglePermission = (permKey) => {
    setNewUser(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permKey)
        ? prev.permissions.filter(p => p !== permKey)
        : [...prev.permissions, permKey]
    }));
  };

  // Toggle multiple permissions at once (for CRUD column clicks)
  const togglePermissionGroup = (permKeys) => {
    setNewUser(prev => {
      const allSelected = permKeys.every(k => prev.permissions.includes(k));
      if (allSelected) {
        return { ...prev, permissions: prev.permissions.filter(p => !permKeys.includes(p)) };
      } else {
        return { ...prev, permissions: [...new Set([...prev.permissions, ...permKeys])] };
      }
    });
  };

  // Toggle all permissions in a category
  const toggleCategoryPermissions = (category) => {
    const categoryPerms = permissionCategories[category] || [];
    const categoryPermKeys = categoryPerms.map(p => p.key);
    const allSelected = categoryPermKeys.every(key => newUser.permissions.includes(key));
    
    if (allSelected) {
      // Deselect all in category
      setNewUser(prev => ({
        ...prev,
        permissions: prev.permissions.filter(p => !categoryPermKeys.includes(p))
      }));
    } else {
      // Select all in category
      setNewUser(prev => ({
        ...prev,
        permissions: [...new Set([...prev.permissions, ...categoryPermKeys])]
      }));
    }
  };

  // Toggle ALL permissions
  const toggleAllPermissions = () => {
    const allPermKeys = availablePermissions.map(p => p.key);
    const allSelected = allPermKeys.every(key => newUser.permissions.includes(key));
    
    if (allSelected) {
      setNewUser(prev => ({ ...prev, permissions: [] }));
    } else {
      setNewUser(prev => ({ ...prev, permissions: allPermKeys }));
    }
  };

  // Check if all permissions in a category are selected
  const isCategoryFullySelected = (category) => {
    const categoryPerms = permissionCategories[category] || [];
    const categoryPermKeys = categoryPerms.map(p => p.key);
    return categoryPermKeys.length > 0 && categoryPermKeys.every(key => newUser.permissions.includes(key));
  };

  // Check if all permissions are selected
  const isAllPermissionsSelected = () => {
    return availablePermissions.length > 0 && availablePermissions.every(p => newUser.permissions.includes(p.key));
  };

  const toggleJurisdiction = (jur) => {
    setNewUser(prev => ({
      ...prev,
      jurisdictions: prev.jurisdictions.includes(jur)
        ? prev.jurisdictions.filter(j => j !== jur)
        : [...prev.jurisdictions, jur]
    }));
  };

  // Filter users
  const filteredUsers = users.filter(u => {
    // Special filter for unverified accounts
    if (searchQuery === 'email_not_verified') {
      return u.email_verified === false;
    }
    const matchesSearch = !searchQuery || 
      u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = filterRole === 'all' || u.role === filterRole;
    const matchesJurisdiction = filterJurisdiction === 'all' || u.jurisdiction === filterJurisdiction;
    
    // Category filter
    const roleData = roles.find(r => r.role === u.role);
    const matchesCategory = filterCategory === 'all' || roleData?.category === filterCategory;
    
    return matchesSearch && matchesRole && matchesJurisdiction && matchesCategory;
  });

  const getRoleInfo = (role) => {
    const roleData = roles.find(r => r.role === role);
    if (roleData) {
      const category = ROLE_CATEGORIES[roleData.category] || ROLE_CATEGORIES.OTHER;
      return {
        value: role,
        label: { fr: t(language, `roles.${role}`) || role, en: role },
        color: category.color,
        category: roleData.category,
        level: roleData.level
      };
    }
    return { value: role, label: { fr: role, en: role }, color: 'bg-gray-500' };
  };
  
  const getClearanceInfo = (level) => CLEARANCE_LEVELS.find(c => c.value === level) || CLEARANCE_LEVELS[0];

  // Group roles by category
  const rolesByCategory = roles.reduce((acc, role) => {
    if (!acc[role.category]) {
      acc[role.category] = [];
    }
    acc[role.category].push(role);
    return acc;
  }, {});

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-user-management">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-3">
              <Users className="w-8 h-8 text-primary" />
              {language === 'fr' ? 'Gestion des Utilisateurs' : 'User Management'}
            </h1>
            <p className="text-muted-foreground mt-1">
              {language === 'fr' 
                ? 'Créez et gérez les comptes, rôles, juridictions et niveaux d\'accès' 
                : 'Create and manage accounts, roles, jurisdictions and access levels'}
            </p>
          </div>
          <Button onClick={() => { resetForm(); setShowCreateModal(true); }} className="gap-2">
            <UserPlus className="w-4 h-4" />
            {language === 'fr' ? 'Nouvel Utilisateur' : 'New User'}
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">{language === 'fr' ? 'Total Utilisateurs' : 'Total Users'}</p>
              <p className="text-3xl font-bold">{users.length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">{language === 'fr' ? 'Actifs' : 'Active'}</p>
              <p className="text-3xl font-bold text-green-600">{users.filter(u => u.active !== false).length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">{language === 'fr' ? 'Administrateurs' : 'Admins'}</p>
              <p className="text-3xl font-bold text-yellow-600">
                {users.filter(u => ['NATIONAL_ADMIN', 'SUPER_ADMIN', 'TECH_SUPPORT'].includes(u.role)).length}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">{language === 'fr' ? 'Accès Classifié' : 'Classified Access'}</p>
              <p className="text-3xl font-bold text-red-600">
                {users.filter(u => u.can_access_classified).length}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Unverified accounts banner */}
        {users.filter(u => u.email_verified === false).length > 0 && (
          <Card className="border-amber-300 bg-amber-50/60 dark:bg-amber-950/20 dark:border-amber-800">
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                <div>
                  <p className="font-semibold text-amber-800 dark:text-amber-200">
                    {users.filter(u => u.email_verified === false).length} {language === 'fr' ? 'compte(s) en attente de vérification email' : 'account(s) pending email verification'}
                  </p>
                  <p className="text-xs text-amber-700 dark:text-amber-300">
                    {language === 'fr' 
                      ? 'Ces utilisateurs ne peuvent pas se connecter. Renvoyez l\'email ou vérifiez manuellement.' 
                      : 'These users cannot log in. Resend the email or manually verify.'}
                  </p>
                </div>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                className="border-amber-400 text-amber-700 hover:bg-amber-100"
                onClick={() => setSearchQuery('email_not_verified')}
                data-testid="filter-unverified-btn"
              >
                <Mail className="w-4 h-4 mr-2" />
                {language === 'fr' ? 'Voir ces comptes' : 'View these accounts'}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder={language === 'fr' ? 'Rechercher...' : 'Search...'}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              
              {/* Category filter */}
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="w-[180px]">
                  <Gavel className="w-4 h-4 mr-2" />
                  <SelectValue placeholder={language === 'fr' ? 'Catégorie' : 'Category'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{language === 'fr' ? 'Toutes catégories' : 'All Categories'}</SelectItem>
                  {Object.entries(ROLE_CATEGORIES).map(([key, cat]) => (
                    <SelectItem key={key} value={key}>
                      {cat.label[language] || cat.label.fr}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={filterRole} onValueChange={setFilterRole}>
                <SelectTrigger className="w-[200px]">
                  <Shield className="w-4 h-4 mr-2" />
                  <SelectValue placeholder={language === 'fr' ? 'Rôle' : 'Role'} />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  <SelectItem value="all">{language === 'fr' ? 'Tous les rôles' : 'All Roles'}</SelectItem>
                  {roles.map(role => (
                    <SelectItem key={role.role} value={role.role}>
                      {t(language, `roles.${role.role}`) || role.role}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filterJurisdiction} onValueChange={setFilterJurisdiction}>
                <SelectTrigger className="w-[180px]">
                  <MapPin className="w-4 h-4 mr-2" />
                  <SelectValue placeholder={language === 'fr' ? 'Juridiction' : 'Jurisdiction'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{language === 'fr' ? 'Toutes' : 'All'}</SelectItem>
                  {JURISDICTIONS.map(j => (
                    <SelectItem key={j.value} value={j.value}>{j.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button variant="outline" onClick={loadUsers}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Users List */}
        <Card>
          <CardHeader>
            <CardTitle>{language === 'fr' ? 'Liste des Utilisateurs' : 'User List'}</CardTitle>
            <CardDescription>{filteredUsers.length} {language === 'fr' ? 'utilisateur(s)' : 'user(s)'}</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {[1,2,3,4,5].map(i => <div key={i} className="h-16 skeleton rounded" />)}
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>{language === 'fr' ? 'Aucun utilisateur trouvé' : 'No users found'}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredUsers.map((u) => {
                  const roleInfo = getRoleInfo(u.role);
                  const clearanceInfo = getClearanceInfo(u.clearance_level);
                  
                  return (
                    <div 
                      key={u.user_id} 
                      className={`p-4 border rounded-lg flex items-center justify-between hover:bg-muted/50 ${u.active === false ? 'opacity-50' : ''}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center text-slate-900 font-bold">
                          {u.name?.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{u.name}</p>
                            {u.active === false && (
                              <Badge variant="destructive" className="text-[10px]">
                                {language === 'fr' ? 'Désactivé' : 'Disabled'}
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">{u.email}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge className={roleInfo.color}>{roleInfo.label[language] || roleInfo.label.fr}</Badge>
                            <Badge variant="outline" className="text-xs">
                              <MapPin className="w-3 h-3 mr-1" />
                              {u.jurisdiction || 'N/A'}
                            </Badge>
                            {u.can_access_classified && (
                              <Badge className={clearanceInfo.color}>
                                <Lock className="w-3 h-3 mr-1" />
                                {clearanceInfo.label[language] || clearanceInfo.label.fr}
                              </Badge>
                            )}
                            {u.converted_from_citizen && (
                              <Badge variant="secondary" className="text-xs bg-green-100 text-green-800">
                                {language === 'fr' ? 'Ex-Citoyen' : 'Former Citizen'}
                              </Badge>
                            )}
                            {u.email_verified === false && (
                              <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-800">
                                <Mail className="w-3 h-3 mr-1" />
                                {language === 'fr' ? 'Email non vérifié' : 'Email not verified'}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {/* Resend verification / Force verify for unverified accounts */}
                        {u.email_verified === false && (
                          <>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleResendVerification(u.user_id, u.email)}
                              title={language === 'fr' ? 'Renvoyer email de vérification' : 'Resend verification email'}
                              className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                              data-testid={`resend-verification-${u.user_id}`}
                            >
                              <Mail className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleForceVerify(u.user_id, u.name || u.email)}
                              title={language === 'fr' ? 'Vérifier manuellement' : 'Manually verify'}
                              className="text-green-600 hover:text-green-700 hover:bg-green-50"
                              data-testid={`force-verify-${u.user_id}`}
                            >
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        {/* Convert citizen to user button */}
                        {u.role === 'CITIZEN' && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => openConvertModal(u)}
                            title={language === 'fr' ? 'Convertir en utilisateur' : 'Convert to user'}
                            className="text-green-600 hover:text-green-700 hover:bg-green-50"
                          >
                            <UserPlus className="w-4 h-4" />
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => handleToggleActive(u.user_id, u.active !== false)}>
                          {u.active !== false ? <Unlock className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleEditUser(u)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteUser(u.user_id)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Convert Citizen to User Modal */}
        <Dialog open={showConvertModal} onOpenChange={setShowConvertModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-green-600" />
                {language === 'fr' ? 'Convertir en Utilisateur' : 'Convert to User'}
              </DialogTitle>
              <DialogDescription>
                {convertingUser && (
                  <span className="font-medium">{convertingUser.name} ({convertingUser.email})</span>
                )}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Nouveau Rôle' : 'New Role'} *</Label>
                <Select value={convertData.role} onValueChange={(v) => setConvertData({...convertData, role: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {roles.filter(r => r.role !== 'CITIZEN').map(role => (
                      <SelectItem key={role.role} value={role.role}>
                        {t(language, `roles.${role.role}`) || role.role}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Niveau d\'habilitation' : 'Clearance Level'}</Label>
                <Select value={convertData.clearance_level} onValueChange={(v) => setConvertData({...convertData, clearance_level: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CLEARANCE_LEVELS.map(level => (
                      <SelectItem key={level.value} value={level.value}>
                        <div className="flex items-center gap-2">
                          <div className={`w-3 h-3 rounded-full ${level.color}`} />
                          {level.label[language] || level.label.fr}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Juridiction' : 'Jurisdiction'}</Label>
                <Select value={convertData.jurisdiction} onValueChange={(v) => setConvertData({...convertData, jurisdiction: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {JURISDICTIONS.map(j => (
                      <SelectItem key={j.value} value={j.value}>{j.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Institution' : 'Institution'}</Label>
                <Select value={convertData.institution || ''} onValueChange={(v) => setConvertData({...convertData, institution: v})}>
                  <SelectTrigger><SelectValue placeholder={language === 'fr' ? 'Sélectionner...' : 'Select...'} /></SelectTrigger>
                  <SelectContent className="max-h-[200px]">
                    {institutions.all?.map(inst => (
                      <SelectItem key={inst} value={inst}>{inst}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowConvertModal(false)}>
                {language === 'fr' ? 'Annuler' : 'Cancel'}
              </Button>
              <Button onClick={handleConvertCitizen} className="gap-2 bg-green-600 hover:bg-green-700">
                <UserPlus className="w-4 h-4" />
                {language === 'fr' ? 'Convertir' : 'Convert'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Create/Edit Modal */}
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent className={editingUser ? "max-w-5xl max-h-[90vh] overflow-y-auto" : "max-w-3xl max-h-[90vh] overflow-y-auto"}>
            <DialogHeader>
              <DialogTitle>
                {editingUser 
                  ? (language === 'fr' ? 'Modifier l\'Utilisateur' : 'Edit User')
                  : (language === 'fr' ? 'Créer un Utilisateur' : 'Create User')}
              </DialogTitle>
              <DialogDescription>
                {language === 'fr' 
                  ? 'Définissez le rôle, la juridiction et les niveaux d\'accès'
                  : 'Set role, jurisdiction and access levels'}
              </DialogDescription>
            </DialogHeader>

            <div className={editingUser ? "grid grid-cols-3 gap-6 mt-4" : "mt-4"}>
              {/* Form Section */}
              <div className={editingUser ? "col-span-2" : ""}>
                <Tabs defaultValue="basic">
                  <TabsList className="w-full">
                    <TabsTrigger value="basic" className="flex-1">{language === 'fr' ? 'Infos' : 'Info'}</TabsTrigger>
                    <TabsTrigger value="access" className="flex-1">{language === 'fr' ? 'Rôle' : 'Role'}</TabsTrigger>
                    <TabsTrigger value="jurisdiction" className="flex-1">{language === 'fr' ? 'Juridiction' : 'Jurisdiction'}</TabsTrigger>
                    <TabsTrigger value="modules" className="flex-1">{language === 'fr' ? 'Modules' : 'Modules'}</TabsTrigger>
                    <TabsTrigger value="permissions" className="flex-1">{language === 'fr' ? 'Permissions' : 'Permissions'}</TabsTrigger>
                  </TabsList>

              {/* Basic Info Tab */}
              <TabsContent value="basic" className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{language === 'fr' ? 'Nom complet' : 'Full Name'} *</Label>
                    <Input 
                      value={newUser.name}
                      onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                      placeholder="Jean Kabila"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email *</Label>
                    <Input 
                      type="email"
                      value={newUser.email}
                      onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                      placeholder="jean.kabila@justice.gouv.cd"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{language === 'fr' ? 'Mot de passe' : 'Password'} {!editingUser && '*'}</Label>
                    <div className="relative">
                      <Input 
                        type={showPassword ? 'text' : 'password'}
                        value={newUser.password}
                        onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                        placeholder={editingUser ? (language === 'fr' ? 'Laisser vide pour ne pas changer' : 'Leave empty to keep') : '••••••••'}
                      />
                      <Button 
                        type="button"
                        variant="ghost" 
                        size="sm" 
                        className="absolute right-0 top-0"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>{language === 'fr' ? 'Téléphone' : 'Phone'}</Label>
                    <Input 
                      value={newUser.phone}
                      onChange={(e) => setNewUser({...newUser, phone: e.target.value})}
                      placeholder="+243 81 XXX XXXX"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Numéro d\'identité nationale' : 'National ID'}</Label>
                  <Input 
                    value={newUser.national_id}
                    onChange={(e) => setNewUser({...newUser, national_id: e.target.value})}
                    placeholder="123456789"
                  />
                </div>

                {/* Grade and Matricule - for professional users */}
                {newUser.role !== 'CITIZEN' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Award className="w-4 h-4" />
                        {language === 'fr' ? 'Grade' : 'Rank'}
                      </Label>
                      <Select 
                        value={newUser.grade || ''} 
                        onValueChange={(v) => setNewUser({...newUser, grade: v})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={language === 'fr' ? 'Sélectionner un grade' : 'Select a rank'} />
                        </SelectTrigger>
                        <SelectContent className="max-h-[250px]">
                          {Object.entries(grades).map(([category, gradeList]) => (
                            <React.Fragment key={category}>
                              <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50">
                                {category}
                              </div>
                              {gradeList.map(grade => (
                                <SelectItem key={grade.value} value={grade.value}>
                                  {language === 'fr' ? grade.label_fr : grade.label_en}
                                </SelectItem>
                              ))}
                            </React.Fragment>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Hash className="w-4 h-4" />
                        {language === 'fr' ? 'Numéro Matricule' : 'Badge Number'}
                      </Label>
                      <Input 
                        value={newUser.matricule || ''}
                        onChange={(e) => setNewUser({...newUser, matricule: e.target.value})}
                        placeholder={language === 'fr' ? 'Ex: POL-2024-0001' : 'Ex: POL-2024-0001'}
                      />
                    </div>
                  </div>
                )}

                {/* Military vs Police */}
                <Separator />
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-green-500" />
                    <Label className="text-base font-medium">
                      {language === 'fr' ? 'Type de Service' : 'Service Type'}
                    </Label>
                  </div>
                  
                  <div className="flex items-center gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 rounded border border-amber-200">
                    <Switch 
                      checked={newUser.is_military}
                      onCheckedChange={(checked) => setNewUser({...newUser, is_military: checked, service_branch: checked ? 'ARMY' : 'POLICE'})}
                    />
                    <Label>
                      {language === 'fr' 
                        ? 'Personnel Militaire (FARDC) - Accès Justice Militaire' 
                        : 'Military Personnel (FARDC) - Military Justice Access'}
                    </Label>
                  </div>

                  <div className="space-y-2">
                    <Label>{language === 'fr' ? 'Branche de Service' : 'Service Branch'}</Label>
                    <Select 
                      value={newUser.service_branch || 'POLICE'} 
                      onValueChange={(v) => setNewUser({...newUser, service_branch: v})}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="POLICE">{language === 'fr' ? 'Police Nationale' : 'National Police'}</SelectItem>
                        <SelectItem value="ARMY">{language === 'fr' ? 'Armée de Terre (FARDC)' : 'Army (FARDC)'}</SelectItem>
                        <SelectItem value="NAVY">{language === 'fr' ? 'Force Navale' : 'Navy'}</SelectItem>
                        <SelectItem value="AIR_FORCE">{language === 'fr' ? 'Force Aérienne' : 'Air Force'}</SelectItem>
                        <SelectItem value="REPUBLICAN_GUARD">{language === 'fr' ? 'Garde Républicaine' : 'Republican Guard'}</SelectItem>
                        <SelectItem value="GENDARMERIE">{language === 'fr' ? 'Gendarmerie' : 'Gendarmerie'}</SelectItem>
                        <SelectItem value="CIVILIAN">{language === 'fr' ? 'Civil' : 'Civilian'}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Separator />

                <div className="flex items-center gap-2 p-3 bg-muted rounded">
                  <Switch 
                    checked={newUser.active}
                    onCheckedChange={(checked) => setNewUser({...newUser, active: checked})}
                  />
                  <Label>{language === 'fr' ? 'Compte actif' : 'Active account'}</Label>
                </div>

                {!editingUser && (
                  <div className="flex items-center gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded border border-blue-200">
                    <Switch 
                      checked={newUser.send_verification !== false}
                      onCheckedChange={(checked) => setNewUser({...newUser, send_verification: checked})}
                      data-testid="send-verification-toggle"
                    />
                    <div>
                      <Label className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-blue-500" />
                        {language === 'fr' ? 'Envoyer un email de vérification' : 'Send verification email'}
                      </Label>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {language === 'fr' 
                          ? 'Le compte restera inactif jusqu\'à ce que l\'utilisateur clique sur le lien de vérification'
                          : 'Account will remain inactive until the user clicks the verification link'}
                      </p>
                    </div>
                  </div>
                )}
              </TabsContent>

              {/* Access & Role Tab */}
              <TabsContent value="access" className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Rôle' : 'Role'} *</Label>
                  <Select value={newUser.role} onValueChange={(v) => setNewUser({...newUser, role: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {Object.entries(rolesByCategory).map(([category, categoryRoles]) => (
                        <React.Fragment key={category}>
                          <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50">
                            {ROLE_CATEGORIES[category]?.label[language] || category}
                          </div>
                          {categoryRoles.map(role => (
                            <SelectItem key={role.role} value={role.role}>
                              <div className="flex items-center gap-2">
                                <div className={`w-3 h-3 rounded-full ${ROLE_CATEGORIES[category]?.color || 'bg-gray-500'}`} />
                                {t(language, `roles.${role.role}`) || role.role}
                                {role.level > 0 && <span className="text-xs text-muted-foreground">(Niv. {role.level})</span>}
                              </div>
                            </SelectItem>
                          ))}
                        </React.Fragment>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Institution selection */}
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Institution de rattachement' : 'Attached Institution'}</Label>
                  <Select value={newUser.institution || ''} onValueChange={(v) => setNewUser({...newUser, institution: v})}>
                    <SelectTrigger><SelectValue placeholder={language === 'fr' ? 'Sélectionner une institution' : 'Select an institution'} /></SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50">
                        {language === 'fr' ? 'Niveau Provincial' : 'Provincial Level'}
                      </div>
                      {institutions.provincial.map(inst => (
                        <SelectItem key={inst} value={inst}>
                          {t(language, `institutions.${inst}`) || inst}
                        </SelectItem>
                      ))}
                      <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50">
                        {language === 'fr' ? 'Niveau National' : 'National Level'}
                      </div>
                      {institutions.national.map(inst => (
                        <SelectItem key={inst} value={inst}>
                          {t(language, `institutions.${inst}`) || inst}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Separator />

                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-red-500" />
                    <Label className="text-base font-medium">
                      {language === 'fr' ? 'Accès aux Documents Classifiés' : 'Classified Document Access'}
                    </Label>
                  </div>

                  {/* Hide classified access option for citizens */}
                  {newUser.role !== 'CITIZEN' && (
                    <>
                      <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 rounded border border-red-200">
                        <Switch 
                          checked={newUser.can_access_classified}
                          onCheckedChange={(checked) => setNewUser({...newUser, can_access_classified: checked})}
                        />
                        <Label>{language === 'fr' ? 'Autoriser l\'accès aux dossiers classifiés' : 'Allow classified file access'}</Label>
                      </div>

                      {newUser.can_access_classified && (
                        <div className="space-y-2">
                          <Label>{language === 'fr' ? 'Niveau d\'habilitation maximum' : 'Maximum Clearance Level'}</Label>
                          <Select 
                            value={newUser.max_classification_access} 
                            onValueChange={(v) => setNewUser({...newUser, max_classification_access: v, clearance_level: v})}
                          >
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {CLEARANCE_LEVELS.map(level => (
                                <SelectItem key={level.value} value={level.value}>
                                  <div className="flex items-center gap-2">
                                    <div className={`w-3 h-3 rounded-full ${level.color}`} />
                                    {level.label[language] || level.label.fr} (Niveau {level.level})
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-muted-foreground">
                            {language === 'fr' 
                              ? 'L\'utilisateur pourra accéder aux documents jusqu\'à ce niveau de classification'
                              : 'User will be able to access documents up to this classification level'}
                          </p>
                        </div>
                      )}
                    </>
                  )}

                  {/* Message for citizens */}
                  {newUser.role === 'CITIZEN' && (
                    <div className="p-3 bg-muted rounded border">
                      <p className="text-sm text-muted-foreground">
                        {language === 'fr' 
                          ? 'Les citoyens n\'ont pas accès aux documents classifiés.' 
                          : 'Citizens do not have access to classified documents.'}
                      </p>
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* Jurisdictions Tab */}
              <TabsContent value="jurisdiction" className="space-y-4 mt-4">
                {/* Territorial Level */}
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Niveau Territorial' : 'Territorial Level'}</Label>
                  <Select 
                    value={newUser.territorial_level || 'NATIONAL'} 
                    onValueChange={(v) => setNewUser({...newUser, territorial_level: v})}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="NATIONAL">{language === 'fr' ? 'National' : 'National'}</SelectItem>
                      <SelectItem value="PROVINCIAL">{language === 'fr' ? 'Provincial' : 'Provincial'}</SelectItem>
                      <SelectItem value="TERRITORIAL">{language === 'fr' ? 'Territorial' : 'Territorial'}</SelectItem>
                      <SelectItem value="COMMUNAL">{language === 'fr' ? 'Communal' : 'Communal'}</SelectItem>
                      <SelectItem value="QUARTIER">{language === 'fr' ? 'Quartier' : 'Neighborhood'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Province - shown when territorial level is not NATIONAL */}
                {newUser.territorial_level !== 'NATIONAL' && (
                  <div className="space-y-2">
                    <Label>{language === 'fr' ? 'Province' : 'Province'}</Label>
                    <Select 
                      value={newUser.province || ''} 
                      onValueChange={(v) => setNewUser({...newUser, province: v})}
                    >
                      <SelectTrigger><SelectValue placeholder={language === 'fr' ? 'Sélectionner une province' : 'Select a province'} /></SelectTrigger>
                      <SelectContent>
                        {JURISDICTIONS.map(j => (
                          <SelectItem key={j.value} value={j.value}>{j.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <Separator />

                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Juridiction principale' : 'Primary Jurisdiction'}</Label>
                  <Select value={newUser.jurisdiction} onValueChange={(v) => setNewUser({...newUser, jurisdiction: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {JURISDICTIONS.map(j => (
                        <SelectItem key={j.value} value={j.value}>{j.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Separator />

                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Juridictions autorisées (multi-sélection)' : 'Allowed Jurisdictions (multi-select)'}</Label>
                  <p className="text-xs text-muted-foreground">
                    {language === 'fr' 
                      ? 'Sélectionnez toutes les juridictions où cet utilisateur peut opérer'
                      : 'Select all jurisdictions where this user can operate'}
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-60 overflow-y-auto p-2 border rounded">
                    {JURISDICTIONS.map(j => (
                      <div key={j.value} className="flex items-center gap-2">
                        <Checkbox 
                          checked={newUser.jurisdictions.includes(j.value)}
                          onCheckedChange={() => toggleJurisdiction(j.value)}
                        />
                        <span className="text-sm">{j.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </TabsContent>

              {/* Modules Access Tab */}
              <TabsContent value="modules" className="space-y-4 mt-4">
                <p className="text-sm text-muted-foreground">
                  {language === 'fr' 
                    ? 'Activez ou desactivez l\'acces aux modules du systeme. Si aucun module n\'est active, l\'utilisateur verra tous les menus selon son role.'
                    : 'Enable or disable access to system modules. If no module is enabled, the user sees all menus based on role.'}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    {code: 'justice_civile', name: language === 'fr' ? 'Justice Civile' : 'Civil Justice', desc: language === 'fr' ? 'Dossiers civils, proces, plaintes' : 'Civil cases, trials, complaints'},
                    {code: 'justice_militaire', name: language === 'fr' ? 'Justice Militaire' : 'Military Justice', desc: language === 'fr' ? 'Dossiers militaires, tribunaux militaires' : 'Military cases, military courts'},
                    {code: 'prisons', name: language === 'fr' ? 'Prisons & Detention' : 'Prisons & Detention', desc: language === 'fr' ? 'Detenus, prisons, cellules, liberations' : 'Inmates, prisons, cells, releases'},
                    {code: 'frontieres', name: language === 'fr' ? 'Controle Frontieres' : 'Border Control', desc: language === 'fr' ? 'Alertes frontieres, biometrie, fugitifs' : 'Border alerts, biometrics, fugitives'},
                    {code: 'police_opj', name: language === 'fr' ? 'Police / OPJ' : 'Police / OPJ', desc: language === 'fr' ? 'Interpellations, gardes a vue, PV' : 'Apprehensions, detentions, reports'},
                    {code: 'bibliotheque_juridique', name: language === 'fr' ? 'Bibliotheque Juridique' : 'Legal Library', desc: language === 'fr' ? 'Textes de loi, infractions, codes' : 'Legal texts, offenses, codes'},
                    {code: 'documents', name: language === 'fr' ? 'Documents & Archives' : 'Documents & Archives', desc: language === 'fr' ? 'Documents juridiques, archives' : 'Legal documents, archives'},
                    {code: 'finances', name: language === 'fr' ? 'Finances & Paiements' : 'Finances & Payments', desc: language === 'fr' ? 'Paiements, frais, amendes' : 'Payments, fees, fines'},
                    {code: 'statistiques', name: language === 'fr' ? 'Statistiques & Rapports' : 'Statistics & Reports', desc: language === 'fr' ? 'Rapports, analytics' : 'Reports, analytics'},
                    {code: 'clemence', name: language === 'fr' ? 'Clemence & Grace' : 'Clemency & Pardon', desc: language === 'fr' ? 'Demandes de grace' : 'Pardon requests'},
                    {code: 'requisitions', name: language === 'fr' ? 'Requisitions' : 'Requisitions', desc: language === 'fr' ? 'Requisitions judiciaires' : 'Judicial requisitions'},
                    {code: 'administration', name: language === 'fr' ? 'Administration' : 'Administration', desc: language === 'fr' ? 'Utilisateurs, structures, config' : 'Users, structures, config'},
                  ].map(mod => {
                    const currentModules = newUser.module_access || [];
                    const isActive = currentModules.includes(mod.code);
                    return (
                      <div key={mod.code} className={`p-3 rounded-lg border cursor-pointer transition-all ${isActive ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/30'}`}
                        onClick={() => {
                          const updated = isActive 
                            ? currentModules.filter(m => m !== mod.code) 
                            : [...currentModules, mod.code];
                          setNewUser({ ...newUser, module_access: updated });
                        }}
                        data-testid={`module-toggle-${mod.code}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium">{mod.name}</p>
                            <p className="text-xs text-muted-foreground">{mod.desc}</p>
                          </div>
                          <div className={`w-5 h-5 rounded border flex items-center justify-center text-xs ${isActive ? 'bg-primary text-white border-primary' : 'border-muted-foreground/30'}`}>
                            {isActive && '✓'}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex gap-2 pt-2">
                  <Button variant="outline" size="sm" onClick={() => {
                    const allCodes = ['justice_civile','justice_militaire','prisons','frontieres','police_opj','bibliotheque_juridique','documents','finances','statistiques','clemence','requisitions','administration'];
                    setNewUser({ ...newUser, module_access: allCodes });
                  }} data-testid="module-select-all">
                    {language === 'fr' ? 'Tout activer' : 'Enable all'}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setNewUser({ ...newUser, module_access: [] })} data-testid="module-deselect-all">
                    {language === 'fr' ? 'Tout desactiver' : 'Disable all'}
                  </Button>
                </div>
              </TabsContent>

              {/* Permissions Tab - CRUD Matrix */}
              <TabsContent value="permissions" className="space-y-4 mt-4">
                <p className="text-sm text-muted-foreground">
                  {language === 'fr' 
                    ? 'Définissez les permissions CRUD pour chaque module (Créer, Voir, Modifier, Supprimer)'
                    : 'Set CRUD permissions for each module (Create, View, Edit, Delete)'}
                </p>
                
                {Object.keys(permissionCategories).length > 0 ? (
                  <div className="space-y-3">
                    {/* Global Select All */}
                    <div className="flex items-center gap-2 p-3 bg-primary/10 rounded-lg border-2 border-primary/30" data-testid="permissions-select-all">
                      <Checkbox 
                        checked={isAllPermissionsSelected()}
                        onCheckedChange={toggleAllPermissions}
                      />
                      <span className="font-semibold text-sm">
                        {language === 'fr' ? 'Tout sélectionner' : 'Select all'}
                      </span>
                      <Badge variant="outline" className="ml-auto text-xs">
                        {newUser.permissions.length}/{availablePermissions.length}
                      </Badge>
                    </div>

                    {/* CRUD Column Headers */}
                    <div className="grid grid-cols-[1fr_50px_50px_50px_50px_1fr] gap-1 px-3 py-2 bg-muted/60 rounded text-[11px] font-semibold text-muted-foreground uppercase tracking-wider sticky top-0 z-10">
                      <span>{language === 'fr' ? 'Module' : 'Module'}</span>
                      <span className="text-center">{language === 'fr' ? 'Voir' : 'View'}</span>
                      <span className="text-center">{language === 'fr' ? 'Créer' : 'Create'}</span>
                      <span className="text-center">{language === 'fr' ? 'Modif.' : 'Edit'}</span>
                      <span className="text-center">{language === 'fr' ? 'Suppr.' : 'Del.'}</span>
                      <span>{language === 'fr' ? 'Actions spéciales' : 'Special actions'}</span>
                    </div>

                    <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
                      {Object.entries(permissionCategories).map(([category, perms]) => {
                        const categoryLabels = {
                          CASES: { fr: 'Dossiers', en: 'Cases' },
                          PRISONERS: { fr: 'Détenus', en: 'Prisoners' },
                          DOCUMENTS: { fr: 'Documents', en: 'Documents' },
                          PAYMENTS: { fr: 'Paiements & Caisse', en: 'Payments & Cash' },
                          BORDER: { fr: 'Frontières', en: 'Border' },
                          REPORTS: { fr: 'Rapports & Stats', en: 'Reports & Stats' },
                          COMPLAINTS: { fr: 'Plaintes', en: 'Complaints' },
                          TRIALS: { fr: 'Procès', en: 'Trials' },
                          INTERPELLATIONS: { fr: 'Interpellations', en: 'Interpellations' },
                          SUMMONS: { fr: 'Convocations & Mandats', en: 'Summons & Warrants' },
                          REQUISITIONS: { fr: 'Réquisitions', en: 'Requisitions' },
                          ADMIN: { fr: 'Administration', en: 'Administration' },
                        };
                        const catLabel = categoryLabels[category]?.[language] || category;
                        const viewPerm = perms.find(p => p.action === 'VIEW');
                        const createPerm = perms.find(p => p.action === 'CREATE');
                        const editPerm = perms.find(p => p.action === 'EDIT');
                        const deletePerm = perms.find(p => p.action === 'DELETE');
                        const managementPerms = perms.filter(p => p.action === 'MANAGE');
                        // Some categories have multiple VIEW/CREATE etc - handle all
                        const viewPerms = perms.filter(p => p.action === 'VIEW');
                        const createPerms = perms.filter(p => p.action === 'CREATE');
                        const editPerms = perms.filter(p => p.action === 'EDIT');
                        const deletePerms = perms.filter(p => p.action === 'DELETE');
                        const selectedCount = perms.filter(p => newUser.permissions.includes(p.key)).length;

                        return (
                          <div key={category} className="border rounded-lg overflow-hidden" data-testid={`permission-category-${category.toLowerCase()}`}>
                            {/* Category Header */}
                            <div className="flex items-center gap-2 px-3 py-2 bg-muted/40 border-b">
                              <Checkbox 
                                checked={isCategoryFullySelected(category)}
                                onCheckedChange={() => toggleCategoryPermissions(category)}
                                data-testid={`select-all-${category.toLowerCase()}`}
                              />
                              <span className="text-xs font-bold text-primary uppercase tracking-wide flex-1">{catLabel}</span>
                              <Badge variant="secondary" className="text-[10px] h-5">
                                {selectedCount}/{perms.length}
                              </Badge>
                            </div>
                            {/* CRUD Row */}
                            <div className="grid grid-cols-[1fr_50px_50px_50px_50px_1fr] gap-1 px-3 py-2 items-center">
                              <span className="text-xs font-medium truncate">{catLabel}</span>
                              {/* View */}
                              <div className="flex justify-center">
                                {viewPerms.length > 0 ? (
                                  <Checkbox 
                                    checked={viewPerms.every(p => newUser.permissions.includes(p.key))}
                                    onCheckedChange={() => togglePermissionGroup(viewPerms.map(p => p.key))}
                                    data-testid={`perm-${category.toLowerCase()}-view`}
                                  />
                                ) : <span className="text-muted-foreground">-</span>}
                              </div>
                              {/* Create */}
                              <div className="flex justify-center">
                                {createPerms.length > 0 ? (
                                  <Checkbox 
                                    checked={createPerms.every(p => newUser.permissions.includes(p.key))}
                                    onCheckedChange={() => togglePermissionGroup(createPerms.map(p => p.key))}
                                    data-testid={`perm-${category.toLowerCase()}-create`}
                                  />
                                ) : <span className="text-muted-foreground">-</span>}
                              </div>
                              {/* Edit */}
                              <div className="flex justify-center">
                                {editPerms.length > 0 ? (
                                  <Checkbox 
                                    checked={editPerms.every(p => newUser.permissions.includes(p.key))}
                                    onCheckedChange={() => togglePermissionGroup(editPerms.map(p => p.key))}
                                    data-testid={`perm-${category.toLowerCase()}-edit`}
                                  />
                                ) : <span className="text-muted-foreground">-</span>}
                              </div>
                              {/* Delete */}
                              <div className="flex justify-center">
                                {deletePerms.length > 0 ? (
                                  <Checkbox 
                                    checked={deletePerms.every(p => newUser.permissions.includes(p.key))}
                                    onCheckedChange={() => togglePermissionGroup(deletePerms.map(p => p.key))}
                                    data-testid={`perm-${category.toLowerCase()}-delete`}
                                  />
                                ) : <span className="text-muted-foreground">-</span>}
                              </div>
                              {/* Special/Manage actions */}
                              <div className="flex flex-wrap gap-1">
                                {managementPerms.map(mp => (
                                  <div key={mp.key} className="flex items-center gap-1">
                                    <Checkbox 
                                      checked={newUser.permissions.includes(mp.key)}
                                      onCheckedChange={() => togglePermission(mp.key)}
                                      className="h-3.5 w-3.5"
                                      data-testid={`perm-${mp.key.replace(/\./g, '-')}`}
                                    />
                                    <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                                      {language === 'fr' ? mp.label_fr?.replace(/^(Voir|Créer|Modifier|Supprimer)\s+(les|des|la|le)?\s*/i, '') : mp.label_en?.replace(/^(View|Create|Edit|Delete)\s*/i, '')}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    {availablePermissions.map(perm => (
                      <div key={perm.key} className="flex items-center gap-2 p-2 border rounded">
                        <Checkbox 
                          checked={newUser.permissions.includes(perm.key)}
                          onCheckedChange={() => togglePermission(perm.key)}
                        />
                        <span className="text-sm">{perm.label[language] || perm.label.fr}</span>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
              </div>

              {/* Profile Card Section - Only shown when editing */}
              {editingUser && (
                <div className="col-span-1">
                  <UserProfileCard 
                    userId={editingUser.user_id} 
                    language={language}
                    showStats={true}
                    showActions={true}
                  />
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
              <Button variant="outline" onClick={() => { setShowCreateModal(false); resetForm(); }}>
                {language === 'fr' ? 'Annuler' : 'Cancel'}
              </Button>
              <Button 
                onClick={editingUser ? handleUpdateUser : handleCreateUser}
                disabled={!newUser.email || !newUser.name || (!editingUser && !newUser.password)}
                className="gap-2"
              >
                <Save className="w-4 h-4" />
                {editingUser 
                  ? (language === 'fr' ? 'Mettre à jour' : 'Update')
                  : (language === 'fr' ? 'Créer' : 'Create')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default AdminUserManagementPage;
